// Dependencies
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";
import { FC, MouseEvent, useState } from "react";

// hooks
import { useUserInfo } from "~/hooks";

// Components
import IconButton from "../IconButton";
import Notification from "../Notification";
import { Badge, Typography } from "@mui/material";

// api
import { getTotalNotify } from "~/services/notification";

// Types
import { QUERY_KEY } from "~/enums";
import { TQueryError } from "~/types";

// Icons
import { ReactComponent as NotifyIcon } from "~/assets/icons/bell-solid.svg";
import { ReactComponent as DropDownIcon } from "~/assets/icons/sort-down-solid.svg";

// Styles
import {
  StyledUserInfo,
  StyledDropdown,
  StyledUserAvatar,
  StyledUserWrapper,
} from "./SideMenu.styled";

interface UserInfoProps {
  notifyPayload: Object | null;
}

const UserInfo: FC<UserInfoProps> = ({ notifyPayload }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const userInfo = useUserInfo();

  const openNoti = Boolean(anchorEl);

  // Query
  const totalNotifyQuery = useQuery({
    queryKey: [QUERY_KEY.NOTIFY.NOTIFY_TOTAL, notifyPayload],
    queryFn: getTotalNotify,
    onError(error: TQueryError) {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const totalNotify = totalNotifyQuery.data?.total || 0;

  /**  Notification */
  const handleOpenNotify = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseNotify = () => {
    setAnchorEl(null);
  };

  return (
    <StyledUserWrapper>
      <StyledUserInfo>
        <StyledUserAvatar />
        <Typography variant={"h4"}>{userInfo.name}</Typography>
        <StyledDropdown>
          <DropDownIcon />
        </StyledDropdown>
      </StyledUserInfo>
      <IconButton
        onClick={handleOpenNotify}
        aria-controls={openNoti ? "notification" : undefined}
        aria-expanded={openNoti ? "true" : undefined}
      >
        <Badge
          invisible={totalNotify === "0" || !totalNotify}
          badgeContent={totalNotify}
          color={"primary"}
        >
          <NotifyIcon />
        </Badge>
      </IconButton>
      <Notification
        open={openNoti}
        anchorEl={anchorEl}
        id="notification"
        onClose={handleCloseNotify}
      />
    </StyledUserWrapper>
  );
};

export default UserInfo;
